/**
 * Record types Universal Search can return. The first nine come from the backend index, the last
 * three are matched in memory against the inspection the user currently has open.
 */
export type UniversalSearchKind = 'inspection' | 'folder' | 'client' | 'site' | 'template' | 'theme' | 'member' | 'role' | 'assignee' | 'category' | 'item' | 'action';

export type UniversalSearchStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UniversalSearchResult {
  kind: UniversalSearchKind;
  id: string;
  title: string;
  subtitle: string;
  /** Router commands for the canonical page of the record. */
  commands: unknown[];
}

export interface UniversalSearchGroup {
  kind: UniversalSearchKind;
  label: string;
  results: UniversalSearchResult[];
}

/** Shortest query the backend index accepts. */
export const REMOTE_SEARCH_MIN_QUERY_LENGTH = 3;

/** Shorter queries still search the open inspection, which costs no reads. */
export const LOCAL_SEARCH_MIN_QUERY_LENGTH = 2;

export const UNIVERSAL_SEARCH_GROUP_ORDER: UniversalSearchKind[] = ['category', 'item', 'action', 'inspection', 'folder', 'client', 'site', 'template', 'theme', 'member', 'role', 'assignee'];

export const UNIVERSAL_SEARCH_GROUP_LABELS: Record<UniversalSearchKind, string> = {
  category: 'Categories',
  item: 'Items',
  action: 'Actions',
  inspection: 'Inspections',
  folder: 'Folders',
  client: 'Clients',
  site: 'Sites',
  template: 'Templates',
  theme: 'Themes',
  member: 'Members',
  role: 'Roles',
  assignee: 'Assignees',
};

export const UNIVERSAL_SEARCH_KIND_LABELS: Record<UniversalSearchKind, string> = {
  category: 'Category',
  item: 'Item',
  action: 'Action',
  inspection: 'Inspection',
  folder: 'Folder',
  client: 'Client',
  site: 'Site',
  template: 'Template',
  theme: 'Theme',
  member: 'Member',
  role: 'Role',
  assignee: 'Assignee',
};

export function groupUniversalSearchResults(results: UniversalSearchResult[]): UniversalSearchGroup[] {
  return UNIVERSAL_SEARCH_GROUP_ORDER.map((kind) => ({
    kind,
    label: UNIVERSAL_SEARCH_GROUP_LABELS[kind],
    results: results.filter((result) => result.kind === kind),
  })).filter((group) => group.results.length > 0);
}

export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\p{S}]+/gu, ' ')
    .trim();
}

/**
 * Latin-script words follow the backend's prefix matching. Other scripts and symbols can match
 * within a token because many writing systems do not separate words with spaces.
 */
export function matchesQuery(values: (string | undefined | null)[], queryWords: string[]): boolean {
  if (queryWords.length === 0) return false;
  const words = normalizeSearchText(values.filter((value): value is string => !!value).join(' ')).split(' ');
  return queryWords.every((queryWord) => {
    const canMatchWithinWord = /[^\p{Script=Latin}\p{N}]/u.test(queryWord);
    return words.some((word) => word.startsWith(queryWord) || (canMatchWithinWord && word.includes(queryWord)));
  });
}

export function toQueryWords(query: string): string[] {
  const normalized = normalizeSearchText(query);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/**
 * Workspace (backend) search normally waits for three characters so short Latin prefixes stay
 * cheap. Emoji and other non-Latin queries are sent as soon as they produce a searchable token,
 * matching how the open-inspection search already behaves.
 */
export function isRemoteSearchQuery(query: string): boolean {
  const trimmed = query.trim();
  if (trimmed.length >= REMOTE_SEARCH_MIN_QUERY_LENGTH) return true;
  if (trimmed.length < LOCAL_SEARCH_MIN_QUERY_LENGTH) return false;
  return toQueryWords(trimmed).some((word) => /[^\p{Script=Latin}\p{N}]/u.test(word));
}
